import React, { useCallback } from 'react';

import { isNil, isEmpty, isString } from 'lodash';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';

import {
  ReferenceInput,
  useRecordContext,
  useUpdate,
  useNotify,
  useRefresh,
  useGetIdentity,
  ReferenceField,
  FunctionField,
  Form,
  RecordContextProvider,
  required,
  AutocompleteInput,
  usePermissions,
} from 'react-admin';

import { useFormContext } from 'react-hook-form';
import { Cancel } from '@mui/icons-material';
import useDialogStatus from '../../../hooks/useDialogStatus';
import hasAccess from '../../../utilities/hasAccess';
import actions from '../../../constants/actions';
import PermissionMissing from '../../utils/PermissionMissing';
import resourceNames from '../../../constants/resourceNames';

const PickMeButton = ({
  source,
  label,
}: {
  source: string;
  label: string;
}) => {
  const { setValue } = useFormContext();
  const { identity } = useGetIdentity();

  const handleAssignMe = useCallback(() => {
    if (identity?.id) {
      setValue(source, identity?.id);
    }
  }, [identity?.id, setValue, source]);

  return (
    <Button onClick={handleAssignMe} size="small">{label}</Button>
  );
};

const DeescalateButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const [update] = useUpdate();

  const handleSubmit = useCallback(() => {
    update(
      'cases',
      {
        id: `${record?.id}/deescalate`,
        data: {
          deescalateFrom: record?.escalation.escalatedTo,
        },
      },
      {
        onSuccess: () => {
          notify('Case have been deescalated.');
          refresh();
          closeDialog();
        },
        onError: () => {
          notify('Could not deescalate the case. This might be because the case has already been escalated to someone else.', { type: 'error' });
          refresh();
        },
      },
    );
  }, [update, record?.id, record?.escalation.escalatedTo, notify, refresh, closeDialog]);

  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth>
        <Form record={{}} onSubmit={handleSubmit}>
          <DialogTitle fontWeight="bold">
            Deescalate case
          </DialogTitle>
          <DialogContent>
            <Typography>
              You are about to deescalate this case from
              {' '}
              <RecordContextProvider value={record}>
                <ReferenceField
                  label="Member"
                  source="escalation.escalatedTo"
                  reference={resourceNames.MEMBERS}
                  link={false}
                >
                  <FunctionField
                    render={(member: {firstName: string; lastName: string}) => `${member.firstName} ${member.lastName}`}
                  />
                </ReferenceField>
              </RecordContextProvider>
              , do you wish to continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Deescalate
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      <Tooltip title="Deescalate">
        <IconButton
          color="primary"
          onClick={openDialog}
        >
          <KeyboardDoubleArrowDownIcon />
        </IconButton>
      </Tooltip>
    </>
  );
};

const EscalateButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const [update] = useUpdate();

  const handleSubmit = useCallback(({
    newEscalatedTo,
  }: { newEscalatedTo?: string }) => {
    update(
      'cases',
      { id: `${record?.id}/escalate`, data: { newEscalatedTo } },
      {
        onSuccess: () => {
          notify('Case have been escalated.');
          refresh();
          closeDialog();
        },
        onError: () => {
          notify('Could not escalate case. This might be because someone else has already escalated the case.', { type: 'error' });
          refresh();
        },
      },
    );
  }, [update, record?.id, notify, refresh, closeDialog]);

  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth>
        <Form record={{}} onSubmit={handleSubmit}>
          <DialogTitle fontWeight="bold">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight="bold">Escalate Case</Typography>
              <IconButton onClick={closeDialog}>
                <Cancel />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              alignItems: 'flex-end',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <PickMeButton source="newEscalatedTo" label="Escalate to me" />
            <ReferenceInput source="newEscalatedTo" reference={resourceNames.MEMBERS} label="Member" filter={{ active: true }}>
              <AutocompleteInput
                fullWidth
                filterSelectedOptions={false}
                filterToQuery={(searchText) => ({ name: searchText })}
                optionText={(item: {firstName: string; lastName: string}) => `${item.firstName} ${item.lastName}`}
                validate={[required()]}
              />
            </ReferenceInput>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Escalate
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      <Tooltip title="Escalate">
        <IconButton
          color="primary"
          onClick={openDialog}
        >
          <KeyboardDoubleArrowUpIcon />
        </IconButton>
      </Tooltip>
    </>
  );
};

const CaseEscalate = ({
  label,
}: {
  label: string;
}) => {
  const record = useRecordContext();

  const { permissions } = usePermissions();
  const canEscalate = hasAccess(permissions, actions.CASE_ESCALATE);

  return (
    <Box display="flex" flexDirection="row" gap={10} alignItems="center" justifyContent="space-between">
      <Box display="flex" flexDirection="column">
        {hasAccess(permissions, actions.MEMBER_GET) ? (
          <ReferenceField emptyText="-" label={label} source="escalation.escalatedTo" reference={resourceNames.MEMBERS} link="edit">
            <FunctionField
              render={(member: {firstName: string; lastName: string}) => `${member.firstName} ${member.lastName}`}
            />
          </ReferenceField>
        ) : (<PermissionMissing />)}
      </Box>
      <>
        {(
          canEscalate
          && (isNil(record?.escalation?.escalatedTo) || isEmpty(record?.escalation?.escalatedTo))
          && <EscalateButton />
        )}
        {canEscalate && isString(record?.escalation?.escalatedTo) && <DeescalateButton />}
      </>
    </Box>
  );
};

export default CaseEscalate;
